<template>
  <div class="rubric-container">
    <div :class="{ 'selected-rubric': selectedRubric }">
      <div v-if="selectedRubric" class="selected-rubric-container">
        <div class="rubric-description">
          <div class="paragraph-s semi-bold">
            <a
              class="paragraph-s semi-bold"
              href="#"
              @click.stop="openRubricPreview(selectedRubric)"
              >{{ selectedRubric?.name }}</a
            >
          </div>

          <div v-if="hasGradeOrLanguage" class="rubric-tags">
            <div v-if="Boolean(selectedRubric.language)" class="eg-tag">
              {{ selectedRubric.language.label }}
            </div>
          </div>
        </div>
        <eg-grading-details-level
          v-if="showGradeLevel && Boolean(selectedRubric.gradeLevel)"
          :grading-details-level="selectedRubric.gradeLevel"
        />
        <div v-if="isSharedRubric" class="rubric-created-by">
          <!-- {{ formatAuthorValue(selectedRubric?.author, $store) || "Me" }} -->
          <icon-badge
            v-if="selectedRubric.shareLink?.isSharedWithDomain"
            v-b-tooltip.hover
            class="mx-1"
            :title="`Anyone with ${userDomain} domain or the link can view`"
          >
            <template #icon>
              <img src="../../../assets/icons/school.svg" alt="" />
            </template>
            {{ $t("rubrics.sharedWithDomain") }}
          </icon-badge>
          <icon-badge
            v-else
            v-b-tooltip.hover
            class="mx-1"
            :title="
              isPlatformRubric
                ? undefined
                : 'Anyone with the private link can view'
            "
          >
            <template #icon>
              <eg-b-icon icon="lock" />
            </template>
            {{ sharedLabel }}
          </icon-badge>
        </div>

        <div class="rubric-actions-container">
          <div v-if="$scopedSlots.actions">
            <slot name="actions" />
          </div>

          <div v-else class="rubric-actions">
            <div v-for="(rubric, index) in rubricsActions" :key="index">
              <eg-button
                size="small"
                :icon="rubric.icon"
                :type="rubric.iconType || 'ordinary'"
                :icon-color="rubric.iconColor"
                @click="rubric.action"
              >
                {{ rubric.label }}
              </eg-button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else
        v-b-toggle.rubric-library-sidebar
        class="rubric-empty-state-text paragraph-s medium rubric-clickable rubric-empty-state"
      >
        <eg-icon icon="plus-add-more-maximize" />
        {{ $t("rubrics.selectRubricToStart") }}
      </div>
    </div>
  </div>
</template>

<script>
import { openRubricPreview } from "./open_rubric_preview";
import { formatAuthorValue } from "@/utils/format";
import IconBadge from "@/components/global/icon_badge.vue";
import EgGradingDetailsLevel from "@/components/global/eg/eg-tags/eg_grading_details_level.vue";

export default {
  name: "EssayRubricSelect",

  components: {
    IconBadge,
    EgGradingDetailsLevel,
  },

  props: {
    selectedRubric: {
      type: Object,
      default: () => {},
    },

    isSelected: {
      type: Boolean,
      default: false,
    },

    isPlatformRubric: {
      type: Boolean,
      default: false,
    },

    showGradeLevel: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {};
  },

  computed: {
    isSharedRubric() {
      return (
        this.$listeners["share-rubric"] &&
        !this.isPlatformRubric &&
        this.selectedRubric?.shareLink
      );
    },

    rubricsActions() {
      const actions = [
        {
          icon: "read-only-view",
          iconType: "ordinary",
          iconColor: "var(--color-icon-neutral-default)",
          action: () => {
            openRubricPreview(this.selectedRubric);
          },
        },
      ];

      if (this.$listeners["edit-rubric"] && !this.isPlatformRubric) {
        actions.push({
          icon: "pen-edit",
          iconType: "ordinary",
          iconColor: "var(--color-icon-neutral-default)",
          action: () => {
            this.$router.push({
              name: "edit-rubric",
              params: { id: this.selectedRubric.id },
            });
          },
        });
      }

      if (this.isSharedRubric) {
        actions.push({
          icon: "collaboration",
          iconType: "ordinary",
          iconColor: "var(--color-icon-neutral-default)",
          action: () => {
            this.$emit("share-rubric", this.selectedRubric);
          },
        });
      }

      if (this.$listeners["select-rubric"]) {
        actions.push({
          icon: "",
          iconType: this.isSelected ? "secondary" : "primary",
          iconColor: "",
          label: this.isSelected
            ? this.$t("rubrics.selected")
            : this.$t("rubrics.select"),
          action: () => {
            this.$emit("select-rubric", this.selectedRubric);
          },
        });
      }

      if (this.$listeners["delete-rubric"] && !this.isPlatformRubric) {
        actions.push({
          icon: "close",
          iconType: "ordinary",
          iconColor: "var(--color-icon-neutral-default)",
          action: () => {
            this.$emit("delete-rubric", this.selectedRubric);
          },
        });
      }

      return actions;
    },

    userDomain() {
      return this.$store.state.user?.domain?.url || "your";
    },

    sharedLabel() {
      if (this.isPlatformRubric) {
        return this.$t("rubrics.anyoneOnEssayGrader");
      }

      if (this.selectedRubric.isOrg) {
        return this.$t("rubrics.organization");
      }

      return this.$t("rubrics.privateLink");
    },

    hasGradeOrLanguage() {
      return Boolean(
        this.selectedRubric.gradeLevel || this.selectedRubric.language
      );
    },
  },

  methods: {
    formatAuthorValue,
    openRubricPreview,
  },
};
</script>

<style scoped lang="scss">
.rubric-container {
  display: flex;
  flex-direction: column;

  .selected-rubric-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    align-content: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  .selected-rubric {
    gap: 8px;
    padding: 16px;
    background-color: white;
    border-radius: var(--radius-rd-400);
    border: 1px solid var(--color-stroke-neutral-default);
    .eg-grading-details-level {
      background-color: transparent;
      border: 1px solid var(--color-stroke-neutral-default);
    }
  }

  .rubric-empty-state {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    border-radius: 16px;
    text-align: center;
    padding: 34px 24px;
    border: 2px dashed var(--color-stroke-primary-default, #8a3ffc);
    cursor: pointer;
    line-height: 20px;
    .rubric-empty-state-text {
      color: var(--color-text-neutral-secondary);
    }
  }

  .rubric-description {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .rubric-info {
    display: flex;
    flex-direction: row;
    gap: 32px;
    align-items: center;
    justify-content: flex-end;
  }

  .rubric-created-by {
    font-family: Inter;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0px;
    color: #6c757d;

    .icon-badge {
      padding: 8px;
      font-size: 12px;
    }
  }

  .rubric-actions {
    display: flex;
    gap: 12px;
  }

  .rubric-tags {
    display: flex;
    gap: 8px;
  }

  @media (max-width: 576px) {
    .selected-rubric-container {
      gap: 8px;
    }
  }
}
</style>
